import { useEffect, useState } from 'react';
import { useAuth } from '../auth/AuthContext';
import { errorMessageFor } from '../auth/error-message';
import { ApiError } from '../api/api-error';
import { adminApi as defaultAdminApi } from './admin-api';
import { generateTemporaryPassword, safeGenerateTemporaryPassword } from '../lib/temporary-password';
import { normalizeDocumentNumber, formatDocumentNumber, isValidDocumentNumber } from '../lib/document-number';
import { isValidPhone } from '../lib/phone';

const DAYS_OPTIONS = [1, 2, 3, 4, 5, 6, 7];
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MIN_PASSWORD_LENGTH = 8;

/**
 * "Nuevo socio" form (spec "Alta y contraseña temporal" + "Administrador").
 * Sends the member fields and optional `coachUserId`; role and activation
 * remain server controlled. Only active coaches are offered for assignment.
 *
 * The temporary password is generated with Web Crypto up front and shown in
 * plain text — this is the "antes de enviar" moment the spec asks for —
 * with a "Regenerar" action and a plain editable field so the admin can type
 * a different one; the server is the sole authority on the real strength
 * rules. It is never written to `console` and never persisted anywhere
 * outside this component's own state.
 */
export function MemberForm({
  api = defaultAdminApi,
  generatePassword = generateTemporaryPassword,
  onCreated,
  onCancel,
}) {
  const { handleApiError } = useAuth();
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [fitnessGoal, setFitnessGoal] = useState('');
  const [targetDaysPerWeek, setTargetDaysPerWeek] = useState('');
  const [documentNumber, setDocumentNumber] = useState('');
  const [useDocumentAsPassword, setUseDocumentAsPassword] = useState(true);
  const [phone, setPhone] = useState('');
  const [coachUserId, setCoachUserId] = useState('');
  const [coaches, setCoaches] = useState([]);
  const [coachesLoading, setCoachesLoading] = useState(Boolean(api.listCoaches));
  const [passwordState, setPasswordState] = useState(() => safeGenerateTemporaryPassword(generatePassword));
  const [formError, setFormError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [documentError, setDocumentError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!api.listCoaches) return undefined;
    let active = true;
    api.listCoaches().then((response) => {
      if (active) setCoaches(((response && response.coaches) || []).filter((coach) => coach.isActive));
    }).catch((error) => {
      const outcome = handleApiError(error);
      if (!outcome.handled && active) setFormError(errorMessageFor(error));
    }).finally(() => { if (active) setCoachesLoading(false); });
    return () => { active = false; };
  }, [api, handleApiError]);

  function regenerate() {
    setPasswordState(safeGenerateTemporaryPassword(generatePassword));
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (submitting) return;
    setFormError('');
    setEmailError('');
    setDocumentError('');

    const trimmedUsername = username.trim();
    const trimmedEmail = email.trim();
    const trimmedPhone = phone.trim();

    if (!trimmedUsername) {
      setFormError('Ingresá un nombre para el socio.');
      return;
    }
    if (!EMAIL_PATTERN.test(trimmedEmail)) {
      setFormError('Ingresá un email válido.');
      return;
    }
    if (!useDocumentAsPassword && (!passwordState.password || passwordState.password.length < MIN_PASSWORD_LENGTH)) {
      setFormError('La contraseña temporal debe tener al menos 8 caracteres.');
      return;
    }
    if (!isValidDocumentNumber(documentNumber)) {
      setFormError(useDocumentAsPassword && !documentNumber
        ? 'Para usar el DNI como contraseña hay que cargar el DNI'
        : 'El DNI debe tener entre 6 y 10 dígitos.');
      return;
    }
    if (!isValidPhone(trimmedPhone)) {
      setFormError('Ingresá un teléfono válido (dígitos, espacios, +, -, ( y )).');
      return;
    }

    const payload = {
      username: trimmedUsername,
      email: trimmedEmail,
      useDocumentAsPassword,
      fitnessGoal: fitnessGoal.trim() || null,
      targetDaysPerWeek: targetDaysPerWeek === '' ? null : Number(targetDaysPerWeek),
      documentNumber: documentNumber || null,
      phone: trimmedPhone || null,
      coachUserId: coachUserId || null,
    };
    if (!useDocumentAsPassword) payload.password = passwordState.password;

    setSubmitting(true);
    try {
      const created = await api.createMember(payload);
      onCreated({ ...created, password: useDocumentAsPassword ? null : payload.password, passwordIsDocument: useDocumentAsPassword });
    } catch (caught) {
      const outcome = handleApiError(caught);
      if (outcome.handled) return;
      if (caught instanceof ApiError && caught.code === 'email_taken') {
        setEmailError(errorMessageFor(caught));
      } else if (caught instanceof ApiError && caught.code === 'document_taken') {
        setDocumentError(errorMessageFor(caught));
      } else {
        setFormError(errorMessageFor(caught));
      }
      setSubmitting(false);
    }
  }

  return (
    <form className="gym-form gym-card gym-admin-member-form" onSubmit={handleSubmit} noValidate aria-label="Nuevo socio">
      {formError && <p role="alert" className="gym-form__error">{formError}</p>}
      {passwordState.error && <p role="alert" className="gym-form__error">{passwordState.error}</p>}

      <div className="gym-field">
        <label htmlFor="member-username" className="gym-field__label">Nombre</label>
        <input
          id="member-username"
          name="username"
          type="text"
          required
          value={username}
          disabled={submitting}
          onChange={(event) => setUsername(event.target.value)}
          className="gym-input"
        />
      </div>

      <div className="gym-field">
        <label htmlFor="member-email" className="gym-field__label">Email</label>
        <input
          id="member-email"
          name="email"
          type="email"
          required
          value={email}
          disabled={submitting}
          onChange={(event) => setEmail(event.target.value)}
          aria-describedby={emailError ? 'member-email-error' : undefined}
          className="gym-input"
        />
        {emailError && <p id="member-email-error" role="alert" className="gym-admin-field-error">{emailError}</p>}
      </div>

      <div className="gym-field">
        <label htmlFor="member-goal" className="gym-field__label">Objetivo (opcional)</label>
        <input
          id="member-goal"
          name="fitnessGoal"
          type="text"
          value={fitnessGoal}
          disabled={submitting}
          onChange={(event) => setFitnessGoal(event.target.value)}
          className="gym-input"
        />
      </div>

      <div className="gym-field">
        <label htmlFor="member-days" className="gym-field__label">Días por semana (opcional)</label>
        <select
          id="member-days"
          name="targetDaysPerWeek"
          value={targetDaysPerWeek}
          disabled={submitting}
          onChange={(event) => setTargetDaysPerWeek(event.target.value)}
          className="gym-input"
        >
          <option value="">Sin definir</option>
          {DAYS_OPTIONS.map((day) => <option key={day} value={day}>{day}</option>)}
        </select>
      </div>

      <div className="gym-field">
        <label htmlFor="member-coach" className="gym-field__label">Profesor asignado (opcional)</label>
        <select id="member-coach" name="coachUserId" value={coachUserId} disabled={submitting || coachesLoading} onChange={(event) => setCoachUserId(event.target.value)} className="gym-input">
          <option value="">Sin profesor</option>
          {coaches.map((coach) => <option key={coach.id} value={coach.id}>{coach.username}</option>)}
        </select>
      </div>

      <div className="gym-field">
        <label htmlFor="member-document" className="gym-field__label">DNI{useDocumentAsPassword ? '' : ' (opcional)'}</label>
        <input
          id="member-document"
          name="documentNumber"
          type="text"
          inputMode="numeric"
          value={formatDocumentNumber(documentNumber)}
          disabled={submitting}
          onChange={(event) => setDocumentNumber(normalizeDocumentNumber(event.target.value))}
          aria-describedby={documentError ? 'member-document-error' : undefined}
          className="gym-input"
        />
        {documentError && <p id="member-document-error" role="alert" className="gym-admin-field-error">{documentError}</p>}
      </div>

      <div className="gym-field">
        <label className="gym-admin-checkbox">
          <input
            type="checkbox"
            name="useDocumentAsPassword"
            checked={useDocumentAsPassword}
            disabled={submitting}
            onChange={(event) => setUseDocumentAsPassword(event.target.checked)}
          />
          Usar el DNI como contraseña
        </label>
      </div>

      <div className="gym-field">
        <label htmlFor="member-phone" className="gym-field__label">Teléfono (opcional)</label>
        <input
          id="member-phone"
          name="phone"
          type="tel"
          inputMode="numeric"
          value={phone}
          disabled={submitting}
          onChange={(event) => setPhone(event.target.value)}
          className="gym-input"
        />
      </div>

      {!useDocumentAsPassword && <div className="gym-field">
        <label htmlFor="member-password" className="gym-field__label">Contraseña temporal</label>
        <div className="gym-admin-password-row">
          <input
            id="member-password"
            name="password"
            type="text"
            autoComplete="off"
            spellCheck="false"
            required
            value={passwordState.password}
            disabled={submitting}
            onChange={(event) => setPasswordState({ password: event.target.value, error: '' })}
            className="gym-input gym-admin-password-row__input"
          />
          <button
            type="button"
            className="gym-button gym-button--secondary"
            disabled={submitting}
            onClick={regenerate}
          >
            Regenerar
          </button>
        </div>
        <p className="gym-admin-field-hint">
          Podés verla y copiarla ahora; después de crear la cuenta se muestra una última vez
          y no se vuelve a mostrar más.
        </p>
      </div>}

      <div className="gym-admin-form-actions">
        <button type="submit" disabled={submitting} className="gym-button gym-button--primary">
          {submitting ? 'Creando…' : 'Crear socio'}
        </button>
        {onCancel && (
          <button type="button" className="gym-button gym-button--secondary" disabled={submitting} onClick={onCancel}>
            Cancelar
          </button>
        )}
      </div>
    </form>
  );
}
